// src/pages/SalesMenu.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";

// أيقونات Material-UI المستوردة
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import PercentIcon from "@mui/icons-material/Percent";
import FactCheckIcon from "@mui/icons-material/FactCheck"; // Import Icon
import SettingsIcon from '@mui/icons-material/Settings';

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function SalesMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth(); // ✅ Use Auth Hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? parseInt(storedCompanyId, 10) : null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setError(t('salesMenu.companyIdNotFound'));
      return;
    }
    setLoading(true);
    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then((res) => setInvoiceTypes(res.data || []))
      .catch((err) => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        setError(t('salesMenu.failedToLoadInvoiceTypes'));
      })
      .finally(() => setLoading(false));
  }, [companyId, t]);

  // دالة مساعدة لتحديد الأيقونة الصحيحة لأنواع الفواتير الديناميكية
  const getInvoiceTypeIcon = (name) => {
    const iconSize = { xs: 32, sm: 36, md: 40 };
    const iconColor = "#f59e0b";
    
    switch (name.trim()) {
      case "بيع":
      case "فاتورة بيع":
        return <ReceiptLongIcon sx={{ fontSize: iconSize, color: iconColor }} />;
      case "مرتجع مبيعات":
      case "فاتورة مرتجع مبيعات":
        return <ReplayIcon sx={{ fontSize: iconSize, color: iconColor }} />;
      case "عرض سعر":
      case "فاتورة عرض سعر":
        return <PriceCheckIcon sx={{ fontSize: iconSize, color: iconColor }} />;
      default:
        return <ReceiptLongIcon sx={{ fontSize: iconSize, color: iconColor }} />;
    }
  };

  // دالة لعرض عناصر الشبكة
  const renderGridItems = (items) => {
    return (
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {items.map((item, index) => (
          <Grid 
            item 
            xs={6} 
            sm={4} 
            md={3} 
            lg={2.4} 
            key={index}
          >
            <ButtonBase
              onClick={() => navigate(item.link)}
              sx={{
                width: "100%",
                height: { xs: 120, sm: 130, md: 140 },
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                border: "2px solid #e0e0e0",
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: { xs: "translateY(-3px)", sm: "translateY(-4px)", md: "translateY(-5px)" },
                  boxShadow: { xs: "0 6px 20px rgba(0,0,0,0.12)", sm: "0 8px 25px rgba(0,0,0,0.15)", md: "0 10px 30px rgba(0,0,0,0.18)" },
                  borderColor: "#1976d2",
                  backgroundColor: "#f8fafc",
                },
                "&:active": {
                  transform: "translateY(0px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={{ xs: 1.5, sm: 2, md: 2.5 }}
                textAlign="center"
              >
                {item.icon}
                <Typography
                  variant="body1"
                  fontWeight="600"
                  mt={{ xs: 1, sm: 1.5 }}
                  color="text.primary"
                  sx={{
                    fontSize: {
                      xs: '0.85rem',
                      sm: '0.95rem',
                      md: '1rem'
                    },
                    lineHeight: 1.3,
                    px: 1
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
    );
  };

  // جلب أنواع الفواتير الخاصة بالمبيعات وتجهيزها
  const salesInvoiceOptions = invoiceTypes
    .filter((type) => {
        // Filter based on type name
        const name = type.name.trim();
        const isSales = ["بيع", "فاتورة بيع"].includes(name);
        const isSalesReturn = ["مرتجع مبيعات", "فاتورة مرتجع مبيعات"].includes(name);
        const isQuotation = ["عرض سعر", "فاتورة عرض سعر"].includes(name);

        if (isSales) return hasPermission(PERMISSIONS.INVOICE_SALES_CREATE);
        if (isSalesReturn) return hasPermission(PERMISSIONS.INVOICE_SALES_RETURN_CREATE);
        if (isQuotation) return hasPermission(PERMISSIONS.INVOICE_QUOTATION_CREATE);

        return false;
    })
    .map((type) => {
      let translationKey = '';
      switch (type.name.trim()) {
        case "بيع":
        case "فاتورة بيع":
          translationKey = 'salesMenu.salesInvoice';
          break;
        case "مرتجع مبيعات":
        case "فاتورة مرتجع مبيعات":
          translationKey = 'salesMenu.salesReturn';
          break;
        case "عرض سعر":
        case "فاتورة عرض سعر":
          translationKey = 'salesMenu.quotation';
          break;
        default:
          translationKey = 'salesMenu.salesInvoice';
      }
      
      return {
        name: t(translationKey),
        icon: getInvoiceTypeIcon(type.name),
        link: `/invoices/new/${type.invoiceTypeId}`,
      };
    });

  // تعريف أقسام القائمة
  const invoiceSection = [
    ...salesInvoiceOptions,
    hasPermission(PERMISSIONS.INVOICE_SALES_CREATE) && {
      name: t('salesMenu.pos') || 'نقطة بيع',
      icon: <PointOfSaleIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#1976d2" }} />,
      link: "/pos"
    },
    hasPermission(PERMISSIONS.CUSTOMER_CREATE) && { 
      name: t('salesMenu.addCustomer'), 
      icon: <PersonAddIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#673ab7" }} />, 
      link: "/customers/new" 
    },
    hasPermission(PERMISSIONS.CUSTOMER_VIEW) && { 
      name: t('salesMenu.customersList'), 
      icon: <PeopleIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#673ab7" }} />, 
      link: "/customers" 
    },
    // POS Settings
    hasPermission(PERMISSIONS.INVOICE_SALES_CREATE) && {
      name: "إعدادات نقطة البيع",
      icon: <SettingsIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#607d8b" }} />,
      link: "/pos-settings"
    },
  ].filter(Boolean); // Remove false items

  const reportSection = [
    hasPermission(PERMISSIONS.INVOICE_SALES_VIEW) && {
      name: t('salesMenu.allSalesInvoices'),
      icon: <ListAltIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#3b82f6" }} />,
      link: "/reports/all-invoices?direction=2&invoiceTypeId=1",
    },
    hasPermission(PERMISSIONS.INVOICE_SALES_RETURN_VIEW) && {
      name: t('salesMenu.allSalesReturns'),
      icon: <ListAltIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#3b82f6" }} />,
      link: "/reports/all-invoices?direction=2&invoiceTypeId=3",
    },
    hasPermission(PERMISSIONS.INVOICE_QUOTATION_VIEW) && {
      name: t('salesMenu.allQuotations'),
      icon: <ListAltIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#3b82f6" }} />,
      link: "/reports/all-invoices?direction=0&invoiceTypeId=10",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.aggregatedItemsSalesReport'),
      icon: <SummarizeIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/aggregated-sales-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.detailedItemsSalesReport'),
      icon: <BarChartIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/detailed-sales-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.dailySalesReport'),
      icon: <CalendarTodayIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/aggregated-sales-ByDay",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.financialTransactions'),
      icon: <CalendarTodayIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/LedgerReport/ledgers",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('salesMenu.VatReportPage'),
      icon: <PercentIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/VatReportPage",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: "تقرير الفوترة الإلكترونية",
      icon: <FactCheckIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/zatca",
    },
  ].filter(Boolean);

  if (loading) {
    return (
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2, md: 3 }
        }} 
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      >
        <Box textAlign="center" py={4}>
          <CircularProgress size={isMobile ? 40 : 50} />
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            sx={{ mt: 2 }}
          >
            {t('salesMenu.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2, md: 3 }
        }} 
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      >
        <Box textAlign="center" py={2}>
          <Typography 
            color="error"
            variant={isMobile ? "body1" : "h6"}
          >
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }} 
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Header مع أزرار التنقل */}
      <Paper 
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4, md: 5 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          <Typography 
            variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
            fontWeight="bold" 
            color="white"
            sx={{
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '2.5rem'
              },
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {t('salesMenu.dashboardTitle')}
          </Typography>

          
        </Box>
      </Paper>

      {/* قسم فواتير المبيعات */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 3, sm: 4, md: 5 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #e3f2fd',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          color="primary"
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #1976d2',
            paddingBottom: 1.5,
          }}
        >
          📋 {t('salesMenu.invoicesSection')}
        </Typography>
        {renderGridItems(invoiceSection)}
      </Paper>
      
      {/* قسم تقارير المبيعات */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #e8f5e9',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          color="success.main"
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #4caf50',
            paddingBottom: 1.5,
          }}
        >
          📊 {t('salesMenu.reportsSection')}
        </Typography>
        {renderGridItems(reportSection)}
      </Paper>
    </Container>
  );
}
